<script setup lang="ts">
import type {Contest} from "@/contest/interfaces";
import {inject, ref} from "vue";
import {CalendarDays, Trophy} from "@lucide/vue";
import dateHelper from "@/core/helpers/dateHelper";
import ContestEdition from "@/contest/components/ContestEdition.vue";
import ContestDuplication from "@/contest/components/ContestDuplication.vue";
import {contestApi} from "@/container";
import {Eye, MoreHorizontal} from "@lucide/vue";
import useEnvironment from "@/notification/composables/useEnvironment";
import * as Sentry from "@sentry/vue";
import {AxiosError} from "axios";
import {Dialog, DialogContent, DialogTitle} from "@/core/components/ui/dialog";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import useContestsProgress, {ContestTableOptions} from "@/contest/composables/useContestsProgress";
import ContestActionDropdownMenu from "@/contest/components/ContestActionDropdownMenu.vue";
import ContestDetailModal from "@/contest/components/ContestDetailModal.vue";
import ContestProgressCell from "@/contest/components/ContestProgressCell.vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";

const emit = defineEmits(['on-refresh']);

const editionOpen = ref(false);
const duplicationOpen = ref(false);
const selectedContestId = ref<number | null>(null);
const detailContest = ref<Contest | null>(null);
const detailOpen = ref(false);
const exportLoading = ref(false);
const exportNotSupported = ref(false);
const exportError = ref(false);

const {environment} = useEnvironment();

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

const selectedStatus = defineModel<null | "upcoming" | "ended">("status", {required: true});
const tableOptions = ref<ContestTableOptions>({
    page: 1,
    itemsPerPage: 10
});
const {contests, totalItems, isLoading, mutate} = useContestsProgress(selectedStatus, tableOptions);


const headers = [
    {title: '', key: 'image', sortable: false, width: '56px'},
    {title: 'Challenge', key: 'name', sortable: false},
    {title: 'Progression', key: 'objective_progress', sortable: false,},
    {title: '', key: 'actions', sortable: false, width: '48px', align: 'end' as const},
];

/**
 * Open detail modal for a contest row click.
 */
const handleRowClick = (_event: Event, {item}: { item: Contest }) => {
    detailContest.value = item;
    detailOpen.value = true;
};

/**
 * Open edition modal.
 */
const openEdition = (contestId: number) => {
    selectedContestId.value = contestId;
    editionOpen.value = true;
};

/**
 * Open duplication modal.
 */
const openDuplication = (contestId: number) => {
    selectedContestId.value = contestId;
    duplicationOpen.value = true;
};


/**
 * Confirm a deletion of a contest.
 */
const confirmDelete = async (contest: Contest) => {
    await openConfirmationModal(
        "Supprimer le challenge",
        "Êtes-vous sûr de vouloir supprimer ce challenge ? Cette action est irréversible.",
        () => contestApi().delete(contest.id).then(() => {
            mutate();
            emit('on-refresh');
        })
    );
};

/**
 * Export contest data.
 */
const exportContest = async (contest: Contest) => {
    exportLoading.value = true;
    exportNotSupported.value = false;
    exportError.value = false;
    try {
        const blob = await contestApi().export(contest.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `export_${contest.name}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (e) {
        if ((e as AxiosError).response?.status === 403) {
            exportNotSupported.value = true;
        } else {
            exportError.value = true;
            Sentry.captureException(e);
        }
    } finally {
        exportLoading.value = false;
    }
};

/**
 * Action when the table options are updated.
 */
const onUpdateOptions = (options: {
    page: number;
    itemsPerPage: number;
}) => {
    tableOptions.value = options;
};
</script>

<template>
  <div class="w-full">
    <v-data-table-server
      v-model:page="tableOptions.page"
      v-model:items-per-page="tableOptions.itemsPerPage"
      :headers="headers"
      :items="contests"
      :items-length="totalItems"
      :loading="isLoading"
      :items-per-page-options="[10, 25, 50]"
      no-data-text="Aucun challenge disponible"
      class="rounded-xl border border-gray-100 cursor-pointer"
      hover
      @click:row="handleRowClick"
      @update:options="onUpdateOptions"
    >
      <template #[`item.image`]="{ item }">
        <div class="py-2">
          <img
            v-if="item.file?.url"
            :src="item.file.url"
            :alt="item.name"
            class="w-10 h-10 object-contain rounded-lg"
          >
          <div v-else class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Trophy class="w-4 h-4 text-primary" />
          </div>
        </div>
      </template>

      <template #[`item.name`]="{ item }">
        <div class="flex flex-col max-w-[200px]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger class="flex">
                <span class="truncate font-medium text-gray-900">{{ item.name }}</span>
              </TooltipTrigger>
              <TooltipContent class="bg-primary text-sm">
                <span class=" text-white">{{ item.name }}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div class="flex items-center gap-1.5 text-sm text-gray-500">
            <CalendarDays class="w-3.5 h-3.5 shrink-0" />
            <span v-if="item.end_date">
            {{ dateHelper.shortFormat(new Date(item.start_date)) }} → {{
                dateHelper.shortFormat(new Date(item.end_date))
              }}
          </span>
            <span v-else>
            Dès le {{ dateHelper.shortFormat(new Date(item.start_date)) }}
          </span>
          </div>
        </div>
      </template>

      <template #[`item.objective_progress`]="{ item }">
        <ContestProgressCell :contest="item" />
      </template>

      <template #[`item.actions`]="{ item }">
        <div @click.stop class="flex flex-row">
          <button
            class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
            @click="event => handleRowClick(event, { item })"
          >
            <Eye class="w-4 h-4" />
          </button>
          <ContestActionDropdownMenu
            v-if="environment?.organizationId && item.organization_id === environment.organizationId"
            :contest="item"
            @on-duplication="openDuplication(item.id)"
            @on-edition="openEdition(item.id)"
            @on-export="exportContest(item)"
            @on-delete="confirmDelete(item)"
          >
            <button class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
              <MoreHorizontal class="w-4 h-4" />
            </button>
          </ContestActionDropdownMenu>
        </div>
      </template>
    </v-data-table-server>

    <!-- Contest detail modal -->
    <ContestDetailModal
      v-model:open="detailOpen"
      :contest="detailContest"
      @on-refresh="mutate(); emit('on-refresh')"
    />

    <Dialog v-model:open="exportLoading">
      <DialogContent>
        <DialogTitle>Export du challenge en cours</DialogTitle>
        <BaseFlexSpinner v-if="!exportNotSupported && !exportError" />
        <div v-if="exportNotSupported" class="text-center">
          <p>L'export n'est pas supporté pour ce challenge</p>
        </div>
        <div v-if="exportError" class="text-center">
          <p>Une erreur est survenue lors de l'export</p>
        </div>
      </DialogContent>
    </Dialog>

    <ContestEdition
      v-if="selectedContestId"
      v-model:open="editionOpen"
      :contest-id="selectedContestId"
      @on-refresh="mutate(); emit('on-refresh')"
    />
    <ContestDuplication
      v-if="selectedContestId"
      v-model:open="duplicationOpen"
      :contest-id="selectedContestId"
      @on-refresh="mutate(); emit('on-refresh')"
    />
  </div>
</template>

